import PropTypes from "prop-types"
import React from "react"
import { useQuery } from "react-query"

import { mutateApi } from "src/utils/api/ApiMethods"

import AvailabilityBar from "./AvailabilityBar"

const inchesToFeet = (inches) => Math.round(inches / 12)
const inchesToSquareFeet = (inches) => Math.round(inches / 144)

const fetchAvailability = async ({ marinaSlug, night }) => {
  const response = await mutateApi({
    url: `/manage/${marinaSlug}/reservations/availability_v2`,
    method: "POST",
    data: {
      check_in_date: night,
      check_out_date: night,
    },
  })
  return response
}

// Map dock_type from API to display name
const getDockTypeDisplayName = (dockType) => {
  const type = dockType?.toLowerCase() || ""
  switch (type) {
    case "dock":
      return "Dockage"
    case "mooring":
      return "Moorage"
    case "storage":
      return "Storage"
    default:
      return "Other"
  }
}

// Determine metric type from space group type
const getMetricType = (spaceGroupType) => {
  if (spaceGroupType === "SpaceGroup::SquareFootage") {
    return "sqft"
  }
  if (
    spaceGroupType === "SpaceGroup::Linear" ||
    spaceGroupType === "SpaceGroup::LinearV2"
  ) {
    return "linear"
  }
  return "count"
}

// Transform API data into the format needed by AvailabilityBar (aggregated)
const transformAvailabilityData = (occupancies, night) => {
  // Group by dock type, then by metric type
  // Structure: { Dockage: { count: { remaining, total }, linear: { remaining, total } }, ... }
  const categories = {}

  Object.values(occupancies).forEach((occ) => {
    const category = getDockTypeDisplayName(occ.dock_type)
    const metricType = getMetricType(occ.type)

    if (!categories[category]) {
      categories[category] = {}
    }

    if (!categories[category][metricType]) {
      categories[category][metricType] = { remaining: 0, total: 0 }
    }

    // Get the night's data
    const nightData =
      occ.nights?.[night] || Object.values(occ.nights || {})[0] || {}

    // Calculate total and remaining based on metric type
    let total = 0
    let occupied = 0

    if (metricType === "sqft") {
      total = inchesToSquareFeet(occ.inventory_total || 0)
      occupied = inchesToSquareFeet(nightData.square_inches || 0)
    } else if (metricType === "linear") {
      total = inchesToFeet(occ.inventory_total || 0)
      occupied = inchesToFeet(nightData.loa || 0)
    } else {
      total = occ.inventory_total || 0
      occupied = nightData.count || 0
    }

    categories[category][metricType].total += total
    categories[category][metricType].remaining += total - occupied
  })

  // Convert to the array format expected by AvailabilityBar
  // Structure: { Dockage: [{ type: 'count', remaining, total }, { type: 'linear', remaining, total }], ... }
  const result = {}

  // Define the order of metric types for consistent display
  const metricOrder = ["count", "linear", "sqft"]

  Object.entries(categories).forEach(([category, metrics]) => {
    const metricsArray = metricOrder
      .filter(
        (metricType) => metrics[metricType] && metrics[metricType].total > 0
      )
      .map((metricType) => ({
        type: metricType,
        remaining: metrics[metricType].remaining,
        total: metrics[metricType].total,
      }))

    if (metricsArray.length > 0) {
      result[category] = metricsArray
    }
  })

  return result
}

// Transform API data into detailed format for the modal (individual space groups)
const transformDetailedData = (occupancies, night) => {
  // Group space groups by dock type
  // Structure: [{ type: 'Dockage', spaceGroups: [{ id, name, total, remaining, unit }] }]
  const typeMap = {}

  Object.entries(occupancies).forEach(([id, occ]) => {
    const typeName = getDockTypeDisplayName(occ.dock_type)
    const unit = getMetricType(occ.type)

    if (!typeMap[typeName]) {
      typeMap[typeName] = []
    }

    // Get the night's data
    const nightData =
      occ.nights?.[night] || Object.values(occ.nights || {})[0] || {}

    // Calculate total and remaining based on unit type
    let total = 0
    let occupied = 0

    if (unit === "sqft") {
      total = inchesToSquareFeet(occ.inventory_total || 0)
      occupied = inchesToSquareFeet(nightData.square_inches || 0)
    } else if (unit === "linear") {
      total = inchesToFeet(occ.inventory_total || 0)
      occupied = inchesToFeet(nightData.loa || 0)
    } else {
      total = occ.inventory_total || 0
      occupied = nightData.count || 0
    }

    typeMap[typeName].push({
      id,
      name: occ.name || `Space Group ${id}`,
      total,
      remaining: total - occupied,
      unit,
    })
  })

  // Convert to array format and order types consistently
  const typeOrder = ["Dockage", "Moorage", "Storage", "Other"]

  return typeOrder
    .filter((typeName) => typeMap[typeName] && typeMap[typeName].length > 0)
    .map((typeName) => ({
      type: typeName,
      spaceGroups: typeMap[typeName],
    }))
}

const AvailabilityBarContainer = ({ marinaSlug, night }) => {
  const { data, isLoading, isError } = useQuery(
    ["nightAvailability", marinaSlug, night],
    () => fetchAvailability({ marinaSlug, night }),
    {
      refetchOnWindowFocus: false,
      staleTime: 30000,
      enabled: Boolean(marinaSlug && night),
      onError: (err) => {
        console.error("Availability fetch error:", err)
      },
    }
  )

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-14 w-full rounded border border-gray-300 bg-white" />
      </div>
    )
  }

  if (isError) {
    console.error("Failed to fetch availability data")
    return null
  }

  if (!data?.occupancies) {
    return null
  }

  // Handle both array (no space groups) and object (has space groups) response
  if (Array.isArray(data.occupancies) && data.occupancies.length === 0) {
    return null
  }

  const availabilityData = transformAvailabilityData(data.occupancies, night)
  const detailedData = transformDetailedData(data.occupancies, night)

  if (Object.keys(availabilityData).length === 0) {
    return null
  }

  return (
    <AvailabilityBar
      availabilityData={availabilityData}
      detailedData={detailedData}
      night={night}
    />
  )
}

AvailabilityBarContainer.propTypes = {
  marinaSlug: PropTypes.string.isRequired,
  night: PropTypes.string.isRequired,
}

export default AvailabilityBarContainer
